// Module: useTerminalCompletion
// Role: keyed DONE submit + bounded confirmation for the CURRENT flow run.
//       «Готово» is drawn only from a fresh read with flow_run.status=completed,
//       never from a mutation payload, shared isSuccess or an already-closed error.
// Uses: api/flow-types
// Used by: screens/flow/FlowScreen

import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  Checkpoint,
  GetFlowResponse,
  SubmitCheckpointRequest,
} from '../../api/flow-types';

/** Same cadence as POLL_PENDING_MS, bounded by TERMINAL_CONFIRM_TIMEOUT_MS. */
export const TERMINAL_CONFIRM_POLL_MS = 2000;
export const TERMINAL_CONFIRM_TIMEOUT_MS = 60_000;
const SUBMIT_RETRY_LIMIT = 3;
const ALREADY_CLOSED_RE = /already|закрыт/i;

export type TerminalCompletionPhase = 'idle' | 'waiting' | 'retry';

interface SubmitOptions {
  onSuccess: () => void;
  onError: (error: Error) => void;
}

interface UseTerminalCompletionArgs {
  slotId: string;
  flow: GetFlowResponse | undefined;
  checkpoint: Checkpoint | null;
  submit: (body: SubmitCheckpointRequest, options: SubmitOptions) => void;
  refetchFlow: () => Promise<unknown>;
}

export function useTerminalCompletion({
  slotId,
  flow,
  checkpoint,
  submit,
  refetchFlow,
}: UseTerminalCompletionArgs) {
  const runId = flow?.flow_run.id ?? null;
  const active = flow?.flow_run.status === 'active';
  const completed = flow?.flow_run.status === 'completed';
  const checkpointId = checkpoint?.kind === 'DONE' ? checkpoint.id : null;
  const key = active && runId && checkpointId
    ? `${slotId}:${runId}:${checkpointId}`
    : null;

  const [phase, setPhase] = useState<TerminalCompletionPhase>('idle');
  const [error, setError] = useState<Error | null>(null);

  const generation = useRef(0);
  const submittedKey = useRef<string | null>(null);
  const submittedRunId = useRef<string | null>(null);
  const retries = useRef(0);
  const timer = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const schedule = useCallback(
    (callback: () => void, delay: number) => {
      clearTimer();
      timer.current = window.setTimeout(callback, delay);
    },
    [clearTimer],
  );

  const startConfirming = useCallback(
    (gen: number) => {
      const deadline = Date.now() + TERMINAL_CONFIRM_TIMEOUT_MS;
      const tick = () => {
        if (gen !== generation.current) return;
        void Promise.resolve(refetchFlow()).catch(() => {});
        if (Date.now() >= deadline) {
          setPhase('retry');
          setError(new Error('FLOW_COMPLETION_TIMEOUT'));
          return;
        }
        schedule(tick, TERMINAL_CONFIRM_POLL_MS);
      };
      tick();
    },
    [refetchFlow, schedule],
  );

  const submitOnce = useRef<(gen: number) => void>(() => {});
  submitOnce.current = (gen) => {
    submit(
      { artefact_path: '' },
      {
        onSuccess: () => {
          if (gen !== generation.current) return;
          retries.current = 0;
          setPhase('waiting');
          startConfirming(gen);
        },
        onError: (submitError) => {
          if (gen !== generation.current) return;
          if (ALREADY_CLOSED_RE.test(String(submitError?.message ?? ''))) {
            startConfirming(gen);
            return;
          }
          if (retries.current >= SUBMIT_RETRY_LIMIT) {
            setError(submitError);
            setPhase('retry');
            return;
          }
          retries.current += 1;
          schedule(() => {
            if (gen === generation.current) submitOnce.current(gen);
          }, 1000 * 2 ** (retries.current - 1));
        },
      },
    );
  };

  useEffect(() => {
    if (!key || completed) return;
    if (submittedKey.current === key) return;

    submittedKey.current = key;
    submittedRunId.current = runId;
    retries.current = 0;
    setError(null);
    setPhase('waiting');
    const gen = ++generation.current;
    submitOnce.current(gen);

    return () => {
      if (generation.current === gen) generation.current += 1;
      submittedKey.current = null;
      clearTimer();
    };
  }, [key, completed, runId, clearTimer]);

  useEffect(() => {
    if (!completed || runId === null || runId !== submittedRunId.current) return;
    clearTimer();
    setPhase('idle');
    setError(null);
  }, [completed, runId, clearTimer]);

  useEffect(
    () => () => {
      generation.current += 1;
      clearTimer();
    },
    [clearTimer],
  );

  const retry = useCallback(() => {
    if (!key) return;
    setError(null);
    setPhase('waiting');
    retries.current = 0;
    submittedKey.current = key;
    submittedRunId.current = runId;
    const gen = ++generation.current;
    submitOnce.current(gen);
  }, [key, runId]);

  return { phase, error, retry };
}
